import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCheckCircle,
  FaClock,
  FaEnvelope,
  FaExclamationTriangle,
  FaHeadset,
  FaPaperPlane,
} from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { submitContactForm } from '../services/api';
import Recaptcha from '../components/Recaptcha';
import './ContactPage.css';

const SUBJECTS = [
  'Question sur un événement',
  'Compte photographe',
  'Forfaits et facturation',
  'Problème technique',
  'Autre demande',
];

const EMPTY_FORM = { name: '', email: '', subject: SUBJECTS[0], message: '', website: '' };

const ContactPage = () => {
  const { user } = useAuth();
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    name: user?.name || '',
    email: user?.email || '',
  });
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [recaptchaError, setRecaptchaError] = useState('');
  const [recaptchaResetKey, setRecaptchaResetKey] = useState(0);

  const update = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: undefined }));
    setResult(null);
  };

  const validate = () => {
    const next = {};

    if (form.name.trim().length < 2) {
      next.name = 'Indiquez votre nom (2 caractères minimum).';
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      next.email = 'Indiquez une adresse e-mail valide.';
    }
    if (form.subject.trim().length < 3) {
      next.subject = 'Choisissez ou saisissez un sujet.';
    }
    if (form.message.trim().length < 10) {
      next.message = 'Votre message doit faire au moins 10 caractères.';
    }
    if (!recaptchaToken) {
      next.recaptcha = 'Confirmez la vérification anti-robot.';
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!validate()) return;

    setSending(true);
    setResult(null);

    try {
      const response = await submitContactForm({
        name: form.name.trim(),
        email: form.email.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
        website: form.website,
        recaptcha_token: recaptchaToken,
      });

      setResult({
        type: 'success',
        text: response?.message || 'Merci, votre message a bien été envoyé.',
      });
      setForm({ ...EMPTY_FORM, name: form.name, email: form.email });
      setRecaptchaToken('');
      setRecaptchaResetKey((key) => key + 1);
    } catch (err) {
      // Laravel returns 422 with a field-keyed errors object.
      if (err?.errors) {
        setErrors(
          Object.fromEntries(
            Object.entries(err.errors).map(([field, messages]) => [field, messages[0]])
          )
        );
      }

      setResult({
        type: 'error',
        text: err?.message || "Votre message n'a pas pu être envoyé. Réessayez dans un instant.",
      });
    } finally {
      setSending(false);
    }
  };

  const remaining = 5000 - form.message.length;

  return (
    <div className="contact-page">
      <section className="contact-hero">
        <div className="contact-hero-icon"><FaEnvelope /></div>
        <p className="contact-eyebrow">Nous joindre</p>
        <h1>Contact</h1>
        <p className="contact-intro">
          Une question sur un événement, un compte photographe ou un forfait PhotoMatch ?
          Envoyez-nous un message et notre équipe vous répondra.
        </p>
      </section>

      <div className="contact-layout">
        <aside className="contact-aside">
          <article className="contact-card">
            <div className="contact-card-icon"><FaHeadset /></div>
            <h2>Support</h2>
            <p>Pour les questions sur votre compte, vos photos ou vos achats.</p>
            <a href="mailto:konanravier@gmail.com">konanravier@gmail.com</a><br />
            <a href="mailto:agboenock78@gmail.com">agboenock78@gmail.com</a>
          </article>

          <article className="contact-card">
            <div className="contact-card-icon"><FaPaperPlane /></div>
            <h2>Ventes</h2>
            <p>Pour les forfaits photographe et les solutions entreprise.</p>
            <a href="mailto:konanravier@gmail.com">konanravier@gmail.com</a><br />
            <a href="mailto:agboenock78@gmail.com">agboenock78@gmail.com</a>
          </article>

          <article className="contact-card">
            <div className="contact-card-icon"><FaClock /></div>
            <h2>Délai de réponse</h2>
            <p>
              Nous traitons les demandes en priorité pendant les jours ouvrables,
              généralement sous 24 à 48 heures.
            </p>
          </article>
        </aside>

        <form className="contact-form" onSubmit={handleSubmit} noValidate>
          <h2>Écrivez-nous</h2>

          {result && (
            <div className={`contact-alert ${result.type}`} role="status">
              {result.type === 'success' ? <FaCheckCircle /> : <FaExclamationTriangle />}
              <span>{result.text}</span>
            </div>
          )}

          <div className="contact-row">
            <div className="contact-field">
              <label htmlFor="contact-name">Nom complet</label>
              <input
                id="contact-name"
                type="text"
                value={form.name}
                onChange={(e) => update('name', e.target.value)}
                className={errors.name ? 'invalid' : ''}
                placeholder="Awa Traoré"
                autoComplete="name"
                disabled={sending}
              />
              {errors.name && <span className="contact-error">{errors.name}</span>}
            </div>

            <div className="contact-field">
              <label htmlFor="contact-email">Adresse e-mail</label>
              <input
                id="contact-email"
                type="email"
                value={form.email}
                onChange={(e) => update('email', e.target.value)}
                className={errors.email ? 'invalid' : ''}
                placeholder="vous@example.com"
                autoComplete="email"
                disabled={sending}
              />
              {errors.email && <span className="contact-error">{errors.email}</span>}
            </div>
          </div>

          <div className="contact-field">
            <label htmlFor="contact-subject">Sujet</label>
            <select
              id="contact-subject"
              value={form.subject}
              onChange={(e) => update('subject', e.target.value)}
              className={errors.subject ? 'invalid' : ''}
              disabled={sending}
            >
              {SUBJECTS.map((subject) => (
                <option key={subject} value={subject}>{subject}</option>
              ))}
            </select>
            {errors.subject && <span className="contact-error">{errors.subject}</span>}
          </div>

          <div className="contact-field">
            <label htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              rows="7"
              value={form.message}
              onChange={(e) => update('message', e.target.value.slice(0, 5000))}
              className={errors.message ? 'invalid' : ''}
              placeholder="Décrivez votre demande en quelques lignes..."
              disabled={sending}
            />
            <div className="contact-meta">
              {errors.message
                ? <span className="contact-error">{errors.message}</span>
                : <span />}
              <span className="contact-counter">{remaining} caractères restants</span>
            </div>
          </div>

          {/* Honeypot: hidden from people, filled in by bots. */}
          <div className="contact-honeypot" aria-hidden="true">
            <label htmlFor="contact-website">Site web</label>
            <input
              id="contact-website"
              type="text"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => update('website', e.target.value)}
            />
          </div>

          <Recaptcha
            resetKey={recaptchaResetKey}
            onChange={(token) => {
              setRecaptchaToken(token || '');
              setErrors((prev) => ({ ...prev, recaptcha: undefined }));
              setRecaptchaError('');
            }}
            onError={setRecaptchaError}
          />
          {(errors.recaptcha || recaptchaError) && (
            <span className="contact-error">{errors.recaptcha || recaptchaError}</span>
          )}

          <button type="submit" className="contact-submit" disabled={sending || !!recaptchaError}>
            <FaPaperPlane />
            {sending ? 'Envoi en cours...' : 'Envoyer le message'}
          </button>

          <p className="contact-privacy">
            Vos informations servent uniquement à répondre à votre demande.
            Consultez notre <Link to="/privacy">politique de confidentialité</Link>.
          </p>
        </form>
      </div>
    </div>
  );
};

export default ContactPage;
